import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Users, Heart, Camera, Zap, Gift, Menu, X } from 'lucide-react';
import '../styles/Welcome.scss';
import { track } from '../services/track';
import { EV } from '../shared/analyticsEvents';
import logotextblack from '../assets/logotextblack.png'
import Lottie from 'lottie-react';
import heroFrontend from '../assets/herofrontend.json';
import heroBackground from '../assets/herobackground.json';
import invitsStudioLogo from '../assets/invits-studio-logo.ico';
import { useAuth } from '../services/auth';
import NavProfileButton from '../components/NavProfileButton';

const POPULAR_EVENT_TYPES = [
  { name: 'Birthday', emoji: '🎂', path: 'birthday-party' },
  { name: 'Wedding', emoji: '💍', path: 'wedding' },
  { name: 'Graduation', emoji: '🎓', path: 'graduation' },
  { name: 'Baby Shower', emoji: '👶', path: 'baby-shower' },
  { name: 'Dinner', emoji: '🍽️', path: 'dinner-party' },
  { name: 'Game Night', emoji: '🎮', path: 'game-night' },
];

const InvitsWelcome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => { track(EV.WELCOME_VIEWED); }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);



  // Gallery images with titles and going counts
  const galleryEvents = [
    { image: '/images/events/1.jpg', title: "Sarah's 21st Birthday 🎂", going: 47 },
    { image: '/images/events/2.jpg', title: "House Party Szn 🏠", going: 89 },
    { image: '/images/events/3.jpg', title: "Wedding Celebration 💍", going: 156 },
    { image: '/images/events/4.jpg', title: "Coachella Pregame 🎵", going: 124 },
    { image: '/images/events/5.jpg', title: "Graduation Bash 🎓", going: 67 },
    { image: '/images/events/6.jpg', title: "Game Night 🎮", going: 18 },
    { image: '/images/events/7.jpg', title: "Beach Party 🏖️", going: 52 },
    { image: '/images/events/8.jpg', title: "Movie Marathon 🎬", going: 28 },
  ];

  // Event type showcase with images
  const eventTypeShowcase = [
    { type: '🎉 House Parties', image: '/images/events/1.jpg', gradient: 'from-purple-500 to-pink-500' },
    { type: '🎂 Birthdays', image: '/images/events/2.jpg', gradient: 'from-orange-500 to-red-500' },
    { type: '💍 Weddings', image: '/images/events/3.jpg', gradient: 'from-rose-500 to-pink-400' },
    { type: '🎵 Concerts', image: '/images/events/4.jpg', gradient: 'from-blue-500 to-purple-500' },
    { type: '🎓 Graduations', image: '/images/events/5.jpg', gradient: 'from-green-500 to-teal-500' },
    { type: '🍽️ Dinners', image: '/images/events/6.jpg', gradient: 'from-yellow-500 to-orange-500' },
    { type: '🎪 Festivals', image: '/images/events/7.jpg', gradient: 'from-pink-500 to-purple-500' },
    { type: '🎮 Game Nights', image: '/images/events/8.jpg', gradient: 'from-indigo-500 to-blue-500' },
  ];



  /**
   * Router navigation, not a page load.
   *
   * This was `window.location.href = path`, which threw the whole SPA away and
   * refetched the bundle on every in-site link — including the primary Create
   * call to action. External destinations still get window.location, because
   * the router cannot route to another origin.
   */
  const handleNavigation = (path) => {
    if (/^https?:\/\//.test(path)) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  /**
   * An event-type chip starts a matching invitation.
   *
   * These pointed at `/create?type=…`. There is no /create route — the page is
   * /create-event — so every chip in the header and the mobile menu fell
   * through the router's catch-all and landed back on this page. CreateEvent
   * reads `?type=` and preselects the event type.
   */
  const handleEventTypeClick = (eventType) => {
    navigate(`/create-invitation?type=${eventType.path}`);
  };

  return (
    <div className="welcome-page-v2">
      {/* Navigation */}
      <nav className="top-nav">
        <div className="nav-container">
          <div className="nav-left">
            <div className="nav-logo" onClick={() => handleNavigation('/')}>
              <img src={logotextblack} alt="Invits" className="logo-img" />
            </div>
            
            {/* Event Types - Desktop */}
            <div className="event-types-nav desktop-only">
              {POPULAR_EVENT_TYPES.map((type, idx) => (
                <button
                  key={idx}
                  className="event-type-btn"
                  onClick={() => handleEventTypeClick(type)}
                >
                  <span className="type-emoji">{type.emoji}</span>
                  <span className="type-name">{type.name}</span>
                </button>
              ))}
              <a href="#cards" className="nav-link">Cards</a>
            </div>
          </div>

          <div className="nav-right">
            {user ? (
              <NavProfileButton userId={user.id} className="desktop-only" onClick={() => handleNavigation('/dashboard')} />
            ) : (
              <button className="nav-link desktop-only" onClick={() => handleNavigation('/login')}>
                Log in
              </button>
            )}
            <button className="btn-get-app desktop-only" onClick={() => window.location.href = 'https://invits.co/app'}>
              Get the app
            </button>
            <button className="btn-create" onClick={() => handleNavigation('/create-invitation')}>
              Create
            </button>
            
            {/* Mobile Menu Toggle */}
            <button className="mobile-menu-btn mobile-only" aria-label="Open menu" onClick={() => setMobileMenuOpen(true)}>
              <Menu size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            {/* The menu covers the nav, so it carries its own close button. */}
            <div className="mobile-menu-top">
              <img src={logotextblack} alt="Invits" className="logo-img" />
              <button className="mobile-menu-close" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
                <X size={24} />
              </button>
            </div>
            {user ? (
              <button className="mobile-menu-item mobile-menu-profile" onClick={() => { handleNavigation('/dashboard'); setMobileMenuOpen(false); }}>
                <NavProfileButton userId={user.id} size={32} />
                Your dashboard
              </button>
            ) : (
              <button className="mobile-menu-item" onClick={() => { handleNavigation('/login'); setMobileMenuOpen(false); }}>
                Log in
              </button>
            )}
            <button className="mobile-menu-item" onClick={() => { window.location.href = 'https://invits.co/app'; setMobileMenuOpen(false); }}>
              Get the app
            </button>
            <a href="#cards" className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>💌 Cards</a>
            <div className="mobile-menu-divider"></div>
            <div className="mobile-menu-header">Event Types</div>
            {POPULAR_EVENT_TYPES.map((type, idx) => (
              <button
                key={idx}
                className="mobile-menu-item"
                onClick={() => { handleEventTypeClick(type); setMobileMenuOpen(false); }}
              >
                {type.emoji} {type.name}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-v2">
        <div className="hero-container">
          {/* Left - Images/Video */}
          <div className="hero-visual">
       <div className="hero-lottie">
  {/* Background Blur Lottie */}
<div className="hero-lottie-wrapper">
  <div className="hero-lottie-layer hero-lottie-bg">
    <Lottie animationData={heroBackground} loop autoplay  rendererSettings={{
    preserveAspectRatio: 'xMidYMid slice',
  }}/>
  </div>

  <div className="hero-lottie-layer hero-lottie-front">
    <Lottie animationData={heroFrontend} loop autoplay  rendererSettings={{
    preserveAspectRatio: 'xMidYMid slice',
  }} />
  </div>
</div>

</div>

            <div className="visual-overlay">
              <div className="overlay-badge">
                <Sparkles size={16} />
                <span>Trusted by thousands</span>
              </div>
            </div>
          </div>

          {/* Right - Text Content */}
          <div className="hero-content">
            <div className="hero-badge">
              <Zap size={14} />
              <span>The easiest way to plan events</span>
            </div>

            <h1 className="hero-title">
              plan events<br />
              <span className="gradient-text">in seconds ⚡</span>
            </h1>

            <p className="hero-description">
              beautiful invites, instant rsvps, group chats & memories.
              everything you need to throw the perfect party
            </p>

            <div className="hero-actions">
              <button className="cta-primary" onClick={() => handleNavigation('/create-invitation')}>
                <Zap size={20} />
                Create free invite
              </button>
              <button className="cta-secondary" onClick={() => window.location.href = 'https://invits.co/app'}>
                Get the app
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Overlapping Gallery */}
      <section className="gallery-section">
        <div className="gallery-container">
          <div className="gallery-scroll">
            {[...galleryEvents, ...galleryEvents].map((event, idx) => (
              <div key={idx} className="gallery-card">
                <div className="gallery-image" style={{ backgroundImage: `url(${event.image})` }}>
                  <div className="gallery-overlay">
                    <div className="overlay-content">
                      <h3>{event.title}</h3>
                      <p><Users size={14} /> {event.going} going</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types Showcase */}
      <section className="event-types-showcase">
        <div className="showcase-header">
          <h2>for every vibe, every occasion</h2>
          <p>from house parties to weddings, we got you 🔥</p>
        </div>

        <div className="showcase-grid">
          {eventTypeShowcase.map((event, idx) => (
            <div key={idx} className="showcase-card">
              <div className="showcase-image" style={{ backgroundImage: `url(${event.image})` }}>
                <div className={`showcase-gradient bg-gradient-to-br ${event.gradient}`}></div>
              </div>
              <div className="showcase-label">
                <h3>{event.type}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-modern">
        <div className="features-container">
          <div className="feature-card large card-1">
            <div className="feature-content">
              <h3>beautiful invites in 1-click 🎨</h3>
              <p>customize backgrounds, fonts, animations & more. make every invite unique</p>
            </div>
            <div className="feature-visual">
              <div className="visual-grid">
                <div className="visual-item" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>🎂</div>
                <div className="visual-item" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>🎉</div>
                <div className="visual-item" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>💍</div>
                <div className="visual-item" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>🎓</div>
              </div>
            </div>
          </div>

          <div className="feature-card medium card-2">
            <div className="card-icon">
              <Users size={32} />
            </div>
            <h3>build communities 👥</h3>
            <p>create groups for your squad, post invites, grow together</p>
          </div>

          <div className="feature-card medium card-3">
            <div className="card-icon">
              <Heart size={32} />
            </div>
            <h3>vibe match ✨</h3>
            <p>find people who match your energy & plan hangouts</p>
          </div>

          <div className="feature-card wide card-4" id="cards">
            <div className="split-content">
              <div>
                <div className="card-icon">
                  <Gift size={32} />
                </div>
                <h3>greeting cards 💌</h3>
                <p>send beautiful cards for any moment, any vibe</p>
              </div>
              <div className="card-samples">
                <div className="sample-card" style={{ background: '#ff6b9d' }}>🎂</div>
                <div className="sample-card" style={{ background: '#c471ed' }}>💝</div>
                <div className="sample-card" style={{ background: '#4facfe' }}>✨</div>
              </div>
            </div>
          </div>

          <div className="feature-card tall card-5">
            <h3>the good stuff 🌟</h3>
            <div className="features-list">
              <div className="list-item">💬 real-time group chats</div>
              <div className="list-item">📸 shared photo albums</div>
              <div className="list-item">🎵 collaborative playlists</div>
              <div className="list-item">⭐ host & guest ratings</div>
              <div className="list-item">🎮 party games included</div>
              <div className="list-item">🔒 smart privacy controls</div>
            </div>
          </div>

          <div className="feature-card large card-6" id="studio">
            <div className="feature-content">
              <div className="card-icon">
                <Camera size={32} />
              </div>
              <h3>invits studio 📸</h3>
              <p>for photographers: AI photo sorting, client sharing, team collaboration & marketing to thousands</p>
              {/* /studio has no route and never had one; this fell through to
                  the catch-all and reloaded the marketing page. Until Studio
                  has a page of its own, the honest destination is the contact
                  form, which is where a photographer enquiry goes anyway. */}
              <button className="studio-btn" onClick={() => handleNavigation('/contact')}>
                Get in touch
              </button>
            </div>
            <div className="studio-mockup">
              <div className="mockup-window">
                <div className="window-bar">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              <div className="window-content">
  <img
    src={invitsStudioLogo}
    alt="Invits Studio"
    className="studio-logo"
  />
</div>

                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-v2">
        <div className="cta-container">
          <div className="cta-emojis">
            <span>🎉</span>
            <span>✨</span>
            <span>🎊</span>
            <span>💫</span>
            <span>🌟</span>
          </div>
          
          <h2>ready to throw the party of the year?</h2>
          <p>create your first invite in 30 seconds</p>
          
          <button className="cta-big" onClick={() => handleNavigation('/signup')}>
            <Sparkles size={24} />
            Get started free
          </button>
          
          <p className="cta-note">upgrade anytime for premium features</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-v2">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <img src={logotextblack} alt="Invits" className="footer-logo" />
              <p>the new way to celebrate together</p>
            </div>

            <div className="footer-column">
              <h4>Product</h4>
              <a href="/create-invitation" onClick={(e) => { e.preventDefault(); handleNavigation('/create-invitation'); }}>Event Invitations</a>
              <a href="#cards">Greeting Cards</a>
              <a href="#studio">Invits Studio</a>
            </div>

            <div className="footer-column">
              <h4>Company</h4>
              <a href="/contact">Contact</a>
            </div>

            <div className="footer-column">
              <h4>Legal</h4>
              <a href="/terms-and-conditions">Terms</a>
              <a href="/privacy-policy">Privacy</a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2026 invits • made with 💜 for the culture</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InvitsWelcome;